import {
  IconStar,
  IconMoodKid,
  IconLanguage,
  IconStarFilled,
  IconGenderFemale,
  IconCalendarEvent,
  IconMessageLanguage,
  IconCircleCheckFilled,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import ProgressBar from '@/components/ProgressBar';
import { SummaryCard } from '@/components/SummaryCard';
import { Switch } from '@/components/Switch';
import { TagCapsule } from '@/components/TagCapsule';
import { LessonStageConfig, CommonStatusConfig } from '@/constants/constant';
import { ERoleAccess, ECommonStatus } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { IChildDetails, IActivityTimeline } from '@/types';
import {
  getAssetUrl,
  formatLastActive,
  formatUnixToLongDate,
  formatSecondsToDuration,
} from '@/utils';

interface IChildDetailsContentProps {
  child?: IChildDetails | null;
  handleBlockChild: () => void;
}

const ChildDetailsContent = ({
  child,
  handleBlockChild,
}: IChildDetailsContentProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();

  const motherTongueValue =
    String(child?.mother_tongue).toLowerCase() === 'true'
      ? formatText('common.yes')
      : String(child?.mother_tongue).toLowerCase() === 'false'
        ? formatText('common.no')
        : (child?.mother_tongue ?? '-');

  const stats = [
    {
      key: 'fullName',
      label: 'children.details.full-name',
      value: child?.full_name || '-',
      Icon: IconMoodKid,
    },
    {
      key: 'gender',
      label: 'children.details.gender',
      value: child?.gender ?? '-',
      Icon: IconGenderFemale,
    },
    {
      key: 'dob',
      label: 'children.details.dob',
      value:
        child?.date_of_birth && child?.age !== undefined
          ? `${formatUnixToLongDate(Number(child.date_of_birth))} (${child.age} ${formatText('children.details.years')})`
          : '-',
      Icon: IconCalendarEvent,
    },
    {
      key: 'motherTongue',
      label: 'children.details.mother-tongue',
      value: motherTongueValue,
      Icon: IconLanguage,
    },
    {
      key: 'dialect',
      label: 'children.details.dialect',
      value: child?.dialect ?? '-',
      Icon: IconMessageLanguage,
    },
    {
      key: 'learningLevel',
      label: 'children.details.learning-level',
      value:
        child?.learning_stage && LessonStageConfig[child.learning_stage]?.label
          ? formatText(LessonStageConfig[child.learning_stage].label)
          : (child?.learning_stage ?? '-'),
      Icon: IconStar,
    },
  ];

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <div className="space-y-10 bg-white w-full">
      {/* Profile */}
      <div className="flex flex-col items-center gap-4">
        <AvatarGroupList
          list={[
            {
              id: child?.id || '',
              name: child?.full_name || '',
              image: getAssetUrl(child?.asset),
            },
          ]}
          size={100}
          avatarClassName="w-25! h-25! text-4xl!"
          className="rounded-full overflow-hidden"
        />
        <div className="text-2xl font-bold text-neutral-primary">
          {child?.full_name}
        </div>

        <div className="flex gap-4">
          {child?.learning_stage && (
            <span
              className={`px-2 py-1 w-fit rounded-full text-xs font-semibold ${
                LessonStageConfig[child.learning_stage]?.className
              }`}
            >
              {LessonStageConfig[child.learning_stage]?.label
                ? formatText(LessonStageConfig[child.learning_stage]?.label)
                : child?.learning_stage}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-indigo-light-50 text-indigo-secondary text-xs font-semibold">
            {formatText('parent.parent-tag')}
          </span>
        </div>

        <div className="flex gap-6 text-sm text-neutral-secondary">
          <div>
            {formatText('children.details.age')}:{' '}
            <span className="text-neutral-primary">
              {child?.age ?? '-'}{' '}
              {child?.age !== undefined
                ? formatText('children.details.years')
                : ''}
            </span>
          </div>
          <div>
            {formatText('children.details.gender')}:{' '}
            <span className="text-neutral-primary capitalize">
              {child?.gender ?? '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2">
        <Switch
          id="child-status"
          checked={child?.status === ECommonStatus.ACTIVE}
          onChange={handleBlockChild}
        />
        <span className="text-sm">
          {formatText('user-management.status')}:{' '}
          <span className="font-medium">
            {child?.status !== undefined &&
              formatText(
                CommonStatusConfig[
                  child.status as keyof typeof CommonStatusConfig
                ]?.label
              )}
          </span>
        </span>
      </div>

      {/* Details as stats row */}
      <div className="grid grid-cols-2 gap-y-6 gap-x-12">
        {stats?.map(({ label, value, Icon }) => (
          <div key={label} className="flex items-start gap-3">
            {/* Icon */}
            <div className="mt-0.5 text-neutral-hover">
              <Icon size={20} />
            </div>

            {/* Text */}
            <div className="flex flex-col gap-1">
              <span className="text-sm leading-5 text-neutral-secondary">
                {formatText(label)}
              </span>
              <span className="text-base font-medium text-neutral-primary capitalize">
                {value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Progress Summary */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-primary">
          {formatText('children.details.progress-summary')}
        </h3>
        <div className="rounded-2xl border border-neutral-light-200 p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-neutral-secondary">
              {formatText('children.details.overall-progress')}
            </span>
            <span className="font-semibold text-neutral-primary">
              {child?.progress_summary?.overall_progress ?? 0}%
            </span>
          </div>
          <div className="mb-6">
            <ProgressBar
              value={child?.progress_summary?.overall_progress ?? 0}
              showPercentage={false}
              barColor="bg-brand-default"
              backgroundColor="bg-neutral-light-50"
              height="h-2"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <SummaryCard
              label={formatText('children.details.stars-earned')}
              value={child?.progress_summary?.stars_earned ?? 0}
              icon={
                <IconStarFilled size={24} className="mx-auto text-yellow-500" />
              }
              bg="bg-warning-light-100"
              text="text-warning-primary"
              sub="text-warning-secondary"
              variant="highlight"
              isLoading={false}
            />
            <SummaryCard
              label={formatText('children.details.games-completed')}
              value={child?.progress_summary?.games_completed ?? 0}
              bg="bg-success-light-50"
              text="text-success-primary"
              sub="text-success-secondary"
              variant="highlight"
              isLoading={false}
            />
            <SummaryCard
              label={formatText('children.details.avg-screen-time')}
              value={formatSecondsToDuration(
                child?.progress_summary?.average_screen_time
              )}
              bg="bg-indigo-light-50"
              text="text-indigo-900"
              sub="text-indigo-secondary"
              variant="highlight"
              isLoading={false}
            />
          </div>
        </div>
      </div>
      {/* Activity Timeline */}
      <div className="inline-flex flex-col gap-4 w-full">
        <div className="border-b border-neutral-200 pb-1">
          <h3 className="text-lg font-semibold text-neutral-primary leading-7">
            {formatText('children.details.activity')}
          </h3>
        </div>

        {/* Timeline */}
        <div className="relative flex flex-col gap-6">
          {child?.activity_timeline?.length ? (
            child?.activity_timeline?.map((a: IActivityTimeline, i: number) => (
              <>
                {/* Vertical line */}
                <div className="absolute left-5 rtl:right-5 rtl:left-auto top-0 h-full w-px bg-neutral-200" />
                <div key={i} className="flex items-center gap-4">
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex p-2 items-center justify-center rounded-full border border-neutral-300 bg-white">
                    <IconCircleCheckFilled
                      size={24}
                      className="text-brand-default"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div className="space-x-2 min-w-0">
                      <span className="text-base font-semibold text-neutral-primary max-w-80 wrap-break-word">
                        {a?.title}
                      </span>
                      {a?.quarters !== null && a?.quarters !== undefined && (
                        <TagCapsule
                          value={`Q${a.quarters}`}
                          className="bg-purple-50 text-brand-default"
                        />
                      )}
                      {a?.rounds !== null && a?.rounds !== undefined && (
                        <TagCapsule
                          value={`R${a.rounds}`}
                          className="bg-neutral-100 text-neutral-primary"
                        />
                      )}
                    </div>
                    <span className="text-sm text-neutral-secondary shrink-0">
                      {formatLastActive(a?.timestamp, formatText)}
                    </span>
                  </div>
                </div>
              </>
            ))
          ) : (
            <div className="flex items-center flex-col gap-3 justify-center w-full px-0 pt-10">
              <CustomImage
                src={'/assets/images/lesson-breakdown-empty.png'}
                alt="noData"
                width={100}
                height={100}
              />
              <p className="text-neutral-900 text-xl font-semibold sm:text-nowrap ">
                {formatText('common.no-data-found')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildDetailsContent;
